import { Injectable } from '@angular/core'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { environment as env } from '../../../../../environments/environment'
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators'
import { combineLatest, Observable, of, throwError } from 'rxjs'
import { Key, Pagination } from './mb-caas'
import { LanguageService } from '@spartacus/core'
import { CountryService } from '../../../site-context/facade/country.service'
import { CmsEnvironmentService } from '../cms-environment.service'
import { CaasIdType } from '../../enums/caas-id-types'
import { MbConstants } from '../../../shared/mb-constants'

@Injectable({
  providedIn: 'root'
})
export class MbCaasService {

  collection = 'release.content'

  constructor (private httpClient: HttpClient,
               private countryService: CountryService,
               private languageService: LanguageService,
               private cmsEnvService: CmsEnvironmentService) {
  }

  getByIds (ids: string[],
            type: CaasIdType,
            keys?: Key[],
            pagination?: Pagination,
            languageOverride?: string,
            countryOverride?: string): Observable<any> {
    if (!ids?.length) {
      return of([])
    }
    const queryKeys = keys?.map(({ key, operator }) => `{ '${key}': ${operator} }`) ?? []
    if (queryKeys.length) {
      queryKeys.push('{ \'identifier\': 1 }')
      queryKeys.push('{ \'locale.country\': 1 }')
    }
    let countryResult: any
    return combineLatest([this.getCollectionUrl(), this.languageService.getActive(), this.countryService.getActive()]).pipe(
      switchMap(([collectionUrl, language, country]) => this.httpClient.get(collectionUrl, {
        headers: {
          Authorization: `Bearer ${env.caasApiKey}`
        },
        params: {
          filter:
            `{
              '$and': [
                {'$or': [
                  ${ids.map(id => `{'${type}': '${id}'}`).join(',')}
                ]},
                {'locale.language': '${languageOverride?.length ? languageOverride : language}'}
                {'locale.country': '${countryOverride?.length ? countryOverride : country}'}
              ]
            }`,
          page: pagination?.page || 1,
          pagesize: pagination?.pageSize || 20,
          keys: queryKeys
        },
      }).pipe(
        shareReplay(1),
        catchError(this.handleError),
        map((result: any) => result?._embedded?.['rh:doc'] ?? []),
        switchMap((results: any) => {
          countryResult = results
          const fallbacks = results
            .filter((result: any) => result?.page?.children?.some((child: any) =>
              child?.children?.some((c: any) => c?.formData?.st_languageFallbackEnabled?.value)))
            .map((page: any) => page?.identifier)
            .filter((id: string) => id)
          const loadFallbacks = results?.some((result: any) => result?.locale?.country !== MbConstants.FALLBACK_COUNTRY)
          return loadFallbacks && fallbacks?.length
            ? this.loadFallbacks(fallbacks, queryKeys)
            : of(results)
        }),
        map(fallbacks => this.replaceFallbackComponents(countryResult, fallbacks))
      ))
    )
  }

  replaceFallbackComponents (countryResults: any[] = [], fallbacks: any[] = []): any[] {
    countryResults = countryResults.map(countryResult => {
      const fallbackResult = fallbacks.find(fallback => fallback.identifier === countryResult.identifier)
      if (!fallbackResult || !fallbackResult?.page?.children?.length) {
        return countryResult
      }
      countryResult.page.children = countryResult.page.children.map((slot: any) => {
        if (!slot?.children?.length) {
          return slot
        }
        const fallbackComponents = fallbacks.flatMap((result: any) =>
          result?.page?.children?.flatMap((child: any) => child?.children ?? [])
        ) ?? []
        slot.children = slot.children.map((child: any) => {
          if (!child?.formData?.st_languageFallbackEnabled?.value) {
            return child
          }
          return fallbackComponents.find((cc: any) => cc?.identifier === child?.identifier) ?? child
        })
        return slot
      })
      return countryResult
    })
    return countryResults
  }

  loadFallbacks (ids: string[], keys: any[]): Observable<any[]> {
    if (!ids?.length) {
      return of([])
    }
    return this.getCollectionUrl().pipe(
      switchMap(collectionUrl => this.httpClient.get(collectionUrl, {
        headers: {
          Authorization: `Bearer ${env.caasApiKey}`
        },
        params: {
          filter:
            `{
              '$and': [
                {'$or': [
                  ${ids.map(id => `{'${CaasIdType.PAGEID}': '${id}'}`).join(',')}
                ]},
                {'locale.language': '${MbConstants.FALLBACK_LANGUAGE}'}
                {'locale.country': '${MbConstants.FALLBACK_COUNTRY}'}
              ]
            }`,
          pagesize: ids?.length || 20,
          keys: keys ?? []
        },
      }).pipe(
        shareReplay(1),
        catchError(this.handleError),
        map((result: any) => result?._embedded?.['rh:doc'] ?? []),
      ))
    )
  }

  getCountries (): Observable<any> {
    return combineLatest([this.getCollectionUrl(), this.languageService.getActive(), this.countryService.getActive()]).pipe(
      switchMap(([collectionUrl, language, country]) => this.httpClient.get(collectionUrl, {
        headers: {
          Authorization: `Bearer ${env.caasApiKey}`
        },
        params: {
          filter:
            `{
              '$and': [
                {'$or': [
                  {'schema': 'countries'}
                ]},
                {'locale.language': '${language}'}
                {'locale.country': '${country}'}
              ]
            }`,
          page: 1,
          pagesize: 100,
          keys: '{ \'formData\': 1 }'
        },
      }).pipe(
        shareReplay(1),
        catchError(this.handleError),
        map((result: any) => result?._embedded?.['rh:doc'] ?? [])
      ))
    )
  }

  handleError (error: HttpErrorResponse): Observable<never> {
    if (error.error instanceof Error) {
      console.error('An error occurred: ', error.error)
    } else {
      console.error(
        `[${error.status}]: ${error.error}`)
    }
    return throwError('Something went wrong! Please try again later.')
  }

  private getCollectionUrl (): Observable<string> {
    return this.cmsEnvService.isFirstSpiritPreview$.pipe(
      map(isFirstSpiritPreview => {
        const collection = isFirstSpiritPreview ? env.previewContent : env.releaseContent
        return `${env.caasbaseUrl}/${env.caasTenantId}/${env.caasProject}.${collection}`
      })
    )
  }
}
